/**
 * Modelo puro de la pantalla de inicio personalizable: qué widgets hay, en
 * qué orden y con qué tamaño. Se guarda en la preferencia sincronizada
 * `inicio-widgets` como JSON: `[{ "id": "por-hacer", "tamano": "normal" }, …]`.
 *
 * Sin React: todas las operaciones devuelven una lista nueva.
 */

export const IDS_WIDGETS = [
  'hoy',
  'por-hacer',
  'fijadas',
  'cuenta-comun',
  'consejos',
  'resumen-mes',
  'deudas',
  'gasto-mes',
  'huchas',
  'meses-cubiertos',
  'pagas-extra',
  'a-bordo',
  'pagos-fijos',
] as const;

/**
 * Tarjetas de la pestaña «Informes»: usan la misma rejilla y el mismo modelo
 * que Inicio, con su propia lista guardada (`informes-widgets`). «Consejos de
 * Gavi» es la misma tarjeta en las dos pantallas.
 */
export const IDS_INFORMES = [
  'informe-ritmo',
  'informe-se-va',
  'informe-se-pasan',
  'informe-ahorro',
  'informe-patrimonio',
  'informe-semana',
  'informe-categoria',
  'informe-ingresos',
  'informe-margen',
  'consejos',
  'informe-diario',
] as const;

export type IdWidget =
  | (typeof IDS_WIDGETS)[number]
  | (typeof IDS_INFORMES)[number];

export const TAMANOS = ['compacto', 'normal', 'grande'] as const;
export type Tamano = (typeof TAMANOS)[number];

/**
 * `ancho: 'media'` (opcional): la tarjeta ocupa media fila y se empareja con
 * la siguiente que también lo sea. Sin el campo, fila entera como siempre:
 * las listas guardadas antes de la rejilla se ven igual.
 */
export type WidgetInicio = { id: IdWidget; tamano: Tamano; ancho?: 'media' };

/** Los tres tamaños que ve la persona en el selector. */
export const TAMANOS_VISIBLES = ['pequena', 'ancha', 'grande'] as const;
export type TamanoVisible = (typeof TAMANOS_VISIBLES)[number];

/**
 * Tamaños que tienen sentido en cada tarjeta (informe de la tropa, 26-oct):
 * «Pequeña» solo donde el contenido cabe en media anchura y «Grande» solo
 * donde enseña algo más. Las tarjetas no cambian por dentro.
 */
export const TAMANOS_PERMITIDOS: Record<IdWidget, readonly TamanoVisible[]> = {
  hoy: ['pequena', 'ancha', 'grande'],
  'por-hacer': ['ancha', 'grande'],
  fijadas: ['pequena', 'ancha', 'grande'],
  'cuenta-comun': ['ancha'],
  consejos: ['pequena', 'ancha'],
  'resumen-mes': ['pequena', 'ancha', 'grande'],
  deudas: ['ancha'],
  'gasto-mes': ['pequena', 'ancha', 'grande'],
  huchas: ['pequena', 'ancha'],
  'meses-cubiertos': ['pequena', 'ancha', 'grande'],
  'pagas-extra': ['ancha'],
  'a-bordo': ['ancha', 'grande'],
  'pagos-fijos': ['pequena', 'ancha', 'grande'],
  'informe-ritmo': ['pequena', 'ancha', 'grande'],
  'informe-se-va': ['pequena', 'ancha', 'grande'],
  'informe-se-pasan': ['ancha', 'grande'],
  'informe-ahorro': ['pequena', 'ancha'],
  'informe-patrimonio': ['pequena', 'ancha'],
  'informe-semana': ['ancha'],
  'informe-categoria': ['ancha', 'grande'],
  'informe-ingresos': ['pequena', 'ancha'],
  'informe-margen': ['pequena', 'ancha'],
  'informe-diario': ['ancha'],
};

/**
 * Tamaño que ve la persona. Un tamaño guardado que la tarjeta ya no admite
 * (p. ej. «grande» en Huchas) se enseña como «Ancha».
 */
export function tamanoVisible(widget: WidgetInicio): TamanoVisible {
  const permitidos = TAMANOS_PERMITIDOS[widget.id];
  const visto: TamanoVisible =
    widget.tamano === 'compacto'
      ? 'pequena'
      : widget.tamano === 'grande'
        ? 'grande'
        : 'ancha';
  return permitidos.includes(visto) ? visto : 'ancha';
}

/**
 * Cómo se pinta: tamaño interno de la tarjeta y si va a media fila. Sale
 * siempre de un tamaño permitido.
 */
export function aspectoWidget(widget: WidgetInicio): {
  tamano: Tamano;
  media: boolean;
} {
  const visto = tamanoVisible(widget);
  if (visto === 'pequena') {
    return { tamano: 'compacto', media: widget.ancho === 'media' };
  }
  return { tamano: visto === 'grande' ? 'grande' : 'normal', media: false };
}

/** Elegir un tamaño en el selector. Uno no permitido no cambia nada. */
export function cambiarTamanoVisible(
  lista: readonly WidgetInicio[],
  id: IdWidget,
  visto: TamanoVisible,
): WidgetInicio[] {
  if (!TAMANOS_PERMITIDOS[id].includes(visto)) {
    return [...lista];
  }
  return lista.map(w => {
    if (w.id !== id) {
      return w;
    }
    if (visto === 'pequena') {
      return { id, tamano: 'compacto', ancho: 'media' };
    }
    return { id, tamano: visto === 'grande' ? 'grande' : 'normal' };
  });
}

/** Una fila de la rejilla: una tarjeta entera o dos medias. */
export type FilaInicio = {
  widgets: WidgetInicio[];
  /**
   * Media tarjeta sola en su fila. Fuera de edición se pinta a fila entera
   * (compacta); al editar deja un hueco «+» a su lado.
   */
  sola: boolean;
};

/**
 * Reparte la lista en filas de dos columnas: dos medias seguidas comparten
 * fila; el resto va a fila entera.
 */
export function filasInicio(lista: readonly WidgetInicio[]): FilaInicio[] {
  const filas: FilaInicio[] = [];
  for (let i = 0; i < lista.length; i++) {
    const w = lista[i];
    if (!aspectoWidget(w).media) {
      filas.push({ widgets: [w], sola: false });
      continue;
    }
    const siguiente = lista[i + 1];
    if (siguiente && aspectoWidget(siguiente).media) {
      filas.push({ widgets: [w, siguiente], sola: false });
      i++;
    } else {
      filas.push({ widgets: [w], sola: true });
    }
  }
  return filas;
}

/** Orden por defecto: el de la pantalla «Inicio» del concepto A (Cartera). */
export const WIDGETS_POR_DEFECTO: readonly WidgetInicio[] = [
  { id: 'hoy', tamano: 'normal' },
  { id: 'a-bordo', tamano: 'normal' },
  { id: 'por-hacer', tamano: 'normal' },
  { id: 'meses-cubiertos', tamano: 'normal' },
  { id: 'pagas-extra', tamano: 'normal' },
  { id: 'fijadas', tamano: 'normal' },
  { id: 'cuenta-comun', tamano: 'normal' },
  { id: 'consejos', tamano: 'normal' },
  { id: 'resumen-mes', tamano: 'normal' },
];

/** Ids de una pantalla y su orden de serie. */
export type Catalogo = {
  ids: readonly IdWidget[];
  porDefecto: readonly WidgetInicio[];
};

export const CATALOGO_INICIO: Catalogo = {
  ids: IDS_WIDGETS,
  porDefecto: WIDGETS_POR_DEFECTO,
};

/** Informes de serie: las cinco preguntas (maqueta aprobada el 27-oct). */
export const INFORMES_POR_DEFECTO: readonly WidgetInicio[] = [
  { id: 'informe-ritmo', tamano: 'grande' },
  { id: 'informe-se-va', tamano: 'normal' },
  { id: 'informe-se-pasan', tamano: 'normal' },
  { id: 'informe-ahorro', tamano: 'compacto', ancho: 'media' },
  { id: 'informe-patrimonio', tamano: 'compacto', ancho: 'media' },
];

export const CATALOGO_INFORMES: Catalogo = {
  ids: IDS_INFORMES,
  porDefecto: INFORMES_POR_DEFECTO,
};

export function esIdWidget(
  valor: unknown,
  catalogo: Catalogo = CATALOGO_INICIO,
): valor is IdWidget {
  return (
    typeof valor === 'string' &&
    (catalogo.ids as readonly string[]).includes(valor)
  );
}

export function esTamano(valor: unknown): valor is Tamano {
  return (
    typeof valor === 'string' && (TAMANOS as readonly string[]).includes(valor)
  );
}

/**
 * Lee la preferencia. Sin valor (o ilegible) → orden por defecto. Una lista
 * vacía es válida (el usuario lo ha quitado todo). Se descartan ids
 * desconocidos y repetidos; un tamaño desconocido pasa a «normal».
 */
export function leerWidgets(
  raw: string | undefined | null,
  catalogo: Catalogo = CATALOGO_INICIO,
): WidgetInicio[] {
  if (raw == null || raw === '') {
    return [...catalogo.porDefecto];
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [...catalogo.porDefecto];
  }
  if (!Array.isArray(parsed)) {
    return [...catalogo.porDefecto];
  }
  const vistos = new Set<IdWidget>();
  const lista: WidgetInicio[] = [];
  for (const item of parsed) {
    // Se acepta también un id suelto ("fijadas") por comodidad.
    const id: unknown =
      typeof item === 'string'
        ? item
        : item && typeof item === 'object'
          ? (item as { id?: unknown }).id
          : null;
    if (!esIdWidget(id, catalogo) || vistos.has(id)) {
      continue;
    }
    vistos.add(id);
    const tamano: unknown =
      item && typeof item === 'object'
        ? (item as { tamano?: unknown }).tamano
        : null;
    const ancho: unknown =
      item && typeof item === 'object'
        ? (item as { ancho?: unknown }).ancho
        : null;
    const valido: Tamano = esTamano(tamano) ? tamano : 'normal';
    lista.push(
      ancho === 'media' && valido === 'compacto'
        ? { id, tamano: valido, ancho: 'media' }
        : { id, tamano: valido },
    );
  }
  return lista;
}

export function escribirWidgets(lista: readonly WidgetInicio[]): string {
  return JSON.stringify(
    lista.map(({ id, tamano, ancho }) =>
      ancho === 'media' ? { id, tamano, ancho } : { id, tamano },
    ),
  );
}

/** Widgets que se pueden añadir (los que no están en la lista). */
export function widgetsDisponibles(
  lista: readonly WidgetInicio[],
  catalogo: Catalogo = CATALOGO_INICIO,
): IdWidget[] {
  const presentes = new Set(lista.map(w => w.id));
  return catalogo.ids.filter(id => !presentes.has(id));
}

/** Añade un widget al final (o en `posicion`). Si ya está, no cambia nada. */
export function anadirWidget(
  lista: readonly WidgetInicio[],
  id: IdWidget,
  tamano: Tamano = 'normal',
  posicion: number = lista.length,
): WidgetInicio[] {
  if (lista.some(w => w.id === id)) {
    return [...lista];
  }
  const i = Math.max(0, Math.min(posicion, lista.length));
  return [...lista.slice(0, i), { id, tamano }, ...lista.slice(i)];
}

export function quitarWidget(
  lista: readonly WidgetInicio[],
  id: IdWidget,
): WidgetInicio[] {
  return lista.filter(w => w.id !== id);
}

export function cambiarTamano(
  lista: readonly WidgetInicio[],
  id: IdWidget,
  tamano: Tamano,
): WidgetInicio[] {
  return lista.map(w => (w.id === id ? { id, tamano } : w));
}

/** Sube (−1) o baja (+1) un widget una posición. En los bordes no hace nada. */
export function moverWidget(
  lista: readonly WidgetInicio[],
  id: IdWidget,
  paso: -1 | 1,
): WidgetInicio[] {
  const i = lista.findIndex(w => w.id === id);
  const j = i + paso;
  if (i < 0 || j < 0 || j >= lista.length) {
    return [...lista];
  }
  const copia = [...lista];
  [copia[i], copia[j]] = [copia[j], copia[i]];
  return copia;
}

/**
 * Arrastrar y soltar: coloca `id` antes o después de `destino`. Mismo
 * contrato que `onReorder` de react-aria (`dropPosition`).
 */
export function reordenarWidget(
  lista: readonly WidgetInicio[],
  id: IdWidget,
  destino: IdWidget,
  posicion: 'before' | 'after',
): WidgetInicio[] {
  const movido = lista.find(w => w.id === id);
  if (!movido || id === destino) {
    return [...lista];
  }
  const resto = lista.filter(w => w.id !== id);
  const i = resto.findIndex(w => w.id === destino);
  if (i < 0) {
    return [...lista];
  }
  const en = posicion === 'after' ? i + 1 : i;
  return [...resto.slice(0, en), movido, ...resto.slice(en)];
}

/**
 * Nuevo orden de las tarjetas que se ven (arrastrar en Inicio): las que no
 * se pintan en este presupuesto (p. ej. «A bordo» sin pareja) se quedan en
 * su sitio de la lista guardada.
 */
export function reordenarVisibles(
  lista: readonly WidgetInicio[],
  orden: readonly IdWidget[],
): WidgetInicio[] {
  const enOrden = new Set(orden);
  const porId = new Map(lista.map(w => [w.id, w]));
  const cola = orden
    .map(id => porId.get(id))
    .filter((w): w is WidgetInicio => w != null);
  if (cola.length !== lista.filter(w => enOrden.has(w.id)).length) {
    return [...lista];
  }
  let i = 0;
  return lista.map(w => (enOrden.has(w.id) ? cola[i++] : w));
}

/** Sube (−1) o baja (+1) una tarjeta entre las que se ven. */
export function moverVisible(
  lista: readonly WidgetInicio[],
  visibles: readonly IdWidget[],
  id: IdWidget,
  paso: -1 | 1,
): WidgetInicio[] {
  const i = visibles.indexOf(id);
  const j = i + paso;
  if (i < 0 || j < 0 || j >= visibles.length) {
    return [...lista];
  }
  const orden = [...visibles];
  [orden[i], orden[j]] = [orden[j], orden[i]];
  return reordenarVisibles(lista, orden);
}

/**
 * Widgets añadidos después de que Vadym ya hubiera guardado su Inicio: se
 * incorporan una sola vez a la lista guardada (detrás de `despues` si está;
 * si no, o con `despues: null`, al principio) y se apuntan en
 * `inicio-widgets-vistos`, para que si luego los quita no vuelvan a salir.
 */
export const WIDGETS_NUEVOS: readonly {
  id: IdWidget;
  despues: IdWidget | null;
}[] = [
  { id: 'meses-cubiertos', despues: 'por-hacer' },
  { id: 'pagas-extra', despues: 'meses-cubiertos' },
  { id: 'hoy', despues: null },
  { id: 'a-bordo', despues: 'hoy' },
];

/** Lee `inicio-widgets-vistos` (JSON array de ids); ilegible → vacío. */
export function leerVistos(raw: string | undefined | null): IdWidget[] {
  if (!raw) {
    return [];
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((v): v is IdWidget => esIdWidget(v))
      : [];
  } catch {
    return [];
  }
}

/**
 * Incorpora los widgets nuevos que aún no se han visto. Devuelve `null` si
 * no hay nada que hacer (todos vistos).
 */
export function incorporarNuevos(
  lista: readonly WidgetInicio[],
  vistos: readonly IdWidget[],
): { lista: WidgetInicio[]; vistos: IdWidget[] } | null {
  const pendientes = WIDGETS_NUEVOS.filter(n => !vistos.includes(n.id));
  if (pendientes.length === 0) {
    return null;
  }
  let nueva = [...lista];
  for (const { id, despues } of pendientes) {
    const i = despues ? nueva.findIndex(w => w.id === despues) : -1;
    nueva = anadirWidget(nueva, id, 'normal', i < 0 ? 0 : i + 1);
  }
  return {
    lista: nueva,
    vistos: [...vistos, ...pendientes.map(n => n.id)],
  };
}

/** Todos los widgets nuevos, como vistos (al guardar o restablecer). */
export function todosVistos(vistos: readonly IdWidget[]): IdWidget[] {
  const faltan = WIDGETS_NUEVOS.map(n => n.id).filter(
    id => !vistos.includes(id),
  );
  return [...vistos, ...faltan];
}
